import { useState } from 'react'

import { Box, Button, Icon, PopoverMenu, Table, Text } from '@factorialco/deprecated-design-system'
import { QuotePricingLineItemFragment } from 'generated/resources/graphql'

import MoneyField from 'modules/core/components/MoneyField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import RolesAndLevelsEntitySelector from 'modules/project_management/components/tables/JobsAndRatesDataCollection/RolesAndLevelsSelector'
import { SelectedLevelItem } from 'modules/project_management/components/tables/JobsAndRatesDataCollection/types'

interface ItemRowTimeAndMaterialProps {
  item: QuotePricingLineItemFragment
  currency: string
  isDraftStatus: boolean
  isJobCatalogTreeEnabled: boolean
  onUpdate: (updates: Partial<QuotePricingLineItemFragment>) => void
  onDelete: () => void
}

const ItemRow = ({
  item,
  currency,
  isDraftStatus,
  isJobCatalogTreeEnabled,
  onUpdate,
  onDelete,
}: ItemRowTimeAndMaterialProps) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const [open, setOpen] = useState(false)
  const [selectedLevel, setSelectedLevel] = useState<SelectedLevelItem[]>([])

  const getDisplayText = () => {
    if (selectedLevel.length === 0 || !selectedLevel[0]) {
      return i18n.t('common.select')
    }

    const level = selectedLevel[0]
    if (level.roleName) {
      return `${level.roleName} ${level.name}`
    }
    return level.name
  }

  return (
    <Table.Row>
      <Table.Cell colSpan={2}>
        <Box paddingLeft="s24" width="s300">
          {isDraftStatus ? (
            <PopoverMenu type="dialog" open={open} onClick={() => setOpen(!open)}>
              <PopoverMenu.Trigger>
                <Box
                  gap="s8"
                  flexDirection="row"
                  justifyContent="spaceBetween"
                  alignItems="center"
                  border={{
                    all: {
                      width: 's2',
                      style: 'solid',
                      color: 'grey400',
                    },
                  }}
                  borderRadius={{
                    all: 'abs008',
                  }}
                  padding="s8"
                  paddingX="s16"
                  cursor="pointer"
                >
                  <Text.Normal ellipsis>{getDisplayText()}</Text.Normal>
                  <Icon.Small icon="caret-down" color="grey600" />
                </Box>
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="center" width="s400" hideWhenDetached>
                <Box gap="s16" alignItems="center">
                  <RolesAndLevelsEntitySelector
                    selectedLevels={selectedLevel}
                    setValues={(partial) => {
                      if (partial.levels) {
                        setSelectedLevel(partial.levels)
                      }
                    }}
                    existingResourceJobLevelIds={[]}
                    isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
                    isSingleSelector
                  />
                </Box>
              </PopoverMenu.Content>
            </PopoverMenu>
          ) : (
            <Text size="300" color="grey1000">
              {item.description || '-'}
              {/* TODO: This will be replaced with the returned level name */}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Box textAlign="right">
          {isDraftStatus ? (
            <Box flexDirection="row" justifyContent="flexEnd">
              <MoneyField
                placeholder="0"
                currency={'hr'} // TODO: Translate
                onChange={(value) => onUpdate({ ['quantity']: Number(value) })}
                value={(item.quantity || 0).toString()}
              />
            </Box>
          ) : (
            <Text size="300" color="grey1000">
              {item.quantity || '-'}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {MoneyWithCurrency({
              optionalDecimals: true,
              amount: item.unitCostCents || 0,
              currency,
              locale,
            })}
          </Text>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Box textAlign="right">
          {isDraftStatus ? (
            <Box flexDirection="row" justifyContent="flexEnd">
              <MoneyField
                placeholder="0"
                currency={currency}
                onChange={(value) => onUpdate({ ['unitPriceCents']: Number(value) * 100 })}
                value={((item.unitPriceCents || 0) / 100).toString()}
              />
            </Box>
          ) : (
            <Text size="300" color="grey1000">
              {MoneyWithCurrency({
                optionalDecimals: true,
                amount: item.unitPriceCents || 0,
                currency,
                locale,
              })}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {`${(item.profitCents || 0) / 100}\u00A0%`}
          </Text>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {MoneyWithCurrency({
              optionalDecimals: true,
              amount: item.totalCostCents || 0,
              currency,
              locale,
            })}
          </Text>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {MoneyWithCurrency({
              optionalDecimals: true,
              amount: item.totalPriceCents || 0,
              currency,
              locale,
            })}
          </Text>
        </Box>
      </Table.Cell>
      {isDraftStatus && (
        <Table.Cell colSpan={1} width={80}>
          <Button type="secondary" iconName="delete" onClick={onDelete} />
        </Table.Cell>
      )}
    </Table.Row>
  )
}

export default ItemRow
